import { useState, useMemo, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../hooks/api";
import { formatCurrency } from "../../../utils/formatNumber";
import { BulkAdPlanActionModal } from "./BulkAdPlanActionForm";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api/index";
import { successToast, errorToast } from "../../../utils/toasts";
import { MONTHLY_BUDGET_MULTIPLIER } from "../../../components/core/blocks/AdPlan/constants";
import BulkActionsTable from "../../merchandising/components/tables/BulkActionsTable";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import Loading from "../../../components/core/blocks/Loading";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { useModal } from "@ebay/nice-modal-react";
import { Center } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { FaPencil } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { FaRegCalendarTimes } from "react-icons/fa";
import { useSimpleCan } from "../../../config/casl/casl";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

export const formatScheduledAction = (adId, startDate, endDate, actionUnit, actionValue, actionType, channel) => {
  return {
    channel: channel === "walmart" ? "walmart" : "AMZ",
    status: "scheduled",
    start_date: `${startDate.split("T")[0]}T00:00:00.000000`,
    end_date: `${endDate.split("T")[0]}T00:00:00.000000`,
    gvadvertisements_set: [adId],
    fields: [
      {
        field_name: "daily_budget",
        change_type: actionUnit === "percent" ? "percentage_change" : "flat_change",
        change_value:
          actionType === "decrease" ?
            actionUnit === "percent" ?
              1 + parseFloat(actionValue * -1) / 100
            : parseFloat(actionValue * -1)
          : actionUnit === "percent" ? 1 + parseFloat(actionValue) / 100
          : parseFloat(actionValue),
      },
      {
        field_name: "monthly_budget",
        change_type: actionUnit === "percent" ? "percentage_change" : "flat_change",
        change_value:
          actionUnit === "percent" ?
            actionType === "decrease" ?
              1 + parseFloat(actionValue * -1) / 100
            : 1 + parseFloat(actionValue) / 100
          : actionType === "decrease" ? parseFloat(actionValue * MONTHLY_BUDGET_MULTIPLIER * -1)
          : parseFloat(actionValue) * MONTHLY_BUDGET_MULTIPLIER,
      },
    ],
  };
};
const ScheduledBudgetSummary = ({ state, marketPlace: { marketPlace }, adId, channel, jumpToTab }) => {
  const { t } = useTranslation(["advertising"]);
  const fmt = useDateFormatter("long");
  const scheduleModal = useModal(BulkAdPlanActionModal);
  const [selectedAction, setSelectedAction] = useState([]);

  const tableRef = useRef();
  const cancelModalRef = useRef();

  const queryClient = useQueryClient();
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  // Fetch scheduled actions data
  const { data: scheduledActions, isLoading } = useFetch(
    ["scheduledActions", adId],
    `/scheduledactions/`,
    {
      advertisement: adId,
      channel: channel === "walmart" ? "walmart" : "AMZ",
    },
    {
      select: (res) => res.data?.results ?? [],
    },
  );

  const deleteHandler = useMutation({
    mutationFn: async (data) => {
      const { id } = data;
      await api.remove(`/api/scheduledactions/${id}/`);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: t(($) => $.advertising["Successfully Cancelled Budget Changes"]),
      });

      setSelectedAction([]);
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.advertising["Failed to Remove Scheduled Budget Changes"]),
        error,
      });
    },
  });

  const createHandler = useMutation({
    mutationFn: async (body) => {
      const reqData = body.data ?? {};
      return await api.post("/api/scheduledactions/", reqData);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.advertising["Updating Media Plan Budget"]),
      });

      setSelectedAction([]);
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.advertising["Failed to Update Media Plan Budget"]),
        error,
      });
    },
  });

  const updateHandler = useMutation({
    mutationFn: async (body) => {
      const reqData = body.data ?? {};
      return await api.put(`/api/scheduledactions/${reqData.id}/`, reqData);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.advertising["Updating Media Plan Budget"]),
      });

      setSelectedAction([]);
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.advertising["Failed to Update Media Plan Budget"]),
        error,
      });
    },
  });

  const processScheduledBudgetUpdate = async (formData, updateId) => {
    const overlappingAction = scheduledActions.some((sa) => {
      if (
        (moment(formData.startDate).isBetween(sa.start_date.slice(0, 10), sa.end_date.slice(0, 10), undefined, "[]") ||
          moment(formData.endDate).isBetween(sa.start_date.slice(0, 10), sa.end_date.slice(0, 10), undefined, "[]")) &&
        // Only consider overlap if a budget update already exists, allow scheduled bid boost alongside media plan budget update
        sa.gvadvertisements_set.length
      ) {
        return true;
      }
      return false;
    });

    // Only apply overlap check in create mode
    if (overlappingAction && !updateId) {
      errorToast({
        message: t(
          ($) =>
            $.advertising[
              "Failed to create scheduled update for {name}. The selected date range collides with a previously scheduled action."
            ],
          { name: state.ad_name },
        ),
      });
      return;
    }

    const updateData = formatScheduledAction(
      state.id,
      formData.startDate,
      formData.endDate,
      formData.actionUnit,
      formData.actionValue,
      formData.actionType,
      channel,
    );

    if (updateId) {
      updateData["id"] = updateId;
      updateHandler.mutate({
        data: updateData,
      });
    } else {
      createHandler.mutate({
        data: updateData,
      });
    }
  };

  const tableData = useMemo(() => {
    if (!scheduledActions?.length) {
      return [];
    }

    const data =
      Object.values(
        scheduledActions?.reduce((acc, a) => {
          // Below avoids grouping overlapping ad scheduled actions together, even if date ranges and change values match
          if (a?.gvadvertisements_set?.length) {
            acc[`${a.start_date}-${a.end_date}-${a.fields[0].change_value}-${a.id}`] =
              acc[`${a.start_date}-${a.end_date}-${a.fields[0].change_value}-${a.id}`] || [];
            acc[`${a.start_date}-${a.end_date}-${a.fields[0].change_value}-${a.id}`].push(a);
            return acc;
          }

          // Below groups keyword and target actions together if change types/values and date ranges all match
          acc[`${a.start_date}-${a.end_date}-${a.fields[0].change_value}`] =
            acc[`${a.start_date}-${a.end_date}-${a.fields[0].change_value}`] || [];
          acc[`${a.start_date}-${a.end_date}-${a.fields[0].change_value}`].push(a);
          return acc;
        }, {}),
      )
        ?.map((a) => {
          return {
            ...a[0],
            entityCount: a.length,
            id: a.map((groupedAction) => groupedAction.id),
          };
        })
        ?.sort((a, b) => new Date(b.start_date) - new Date(a.start_date)) ?? [];

    return data;
  }, [scheduledActions]);

  if (isLoading) {
    return <Loading fullPage={false} height={"500px"} />;
  }

  return (
    <>
      <Row>
        <Col xs={12}>
          {scheduledActions?.length === 0 ?
            <p className="text-center p-3">
              {t(($) => $.advertising["There are no changes currently scheduled for this media plan."])}
            </p>
          : null}
          <Center>
            <Button
              isDisabled={state.servingStatus === "ENDED" || !userCanUpdateAds}
              onPress={() => {
                scheduleModal.show({
                  title: t(($) => $.advertising["Schedule Budget Change"]),
                  onConfirm: processScheduledBudgetUpdate,
                  actionTarget: t(($) => $.advertising["Daily Budget"]),
                  schedulingDialogue: true,
                });
              }}
            >
              {t(($) => $.advertising["Schedule Budget Change"])}
            </Button>
          </Center>
          <div className={`d-flex ${scheduledActions?.length ? "justify-content-end" : "justify-content-center"}`}>
            {scheduledActions?.length ?
              <ConfirmationModal
                title={t(($) => $.advertising["Cancel Schedule Budget Update"])}
                onConfirm={() => {
                  if (typeof selectedAction === "object") {
                    for (let i = 0; i < selectedAction?.length; i++) {
                      deleteHandler.mutate({
                        id: selectedAction[i],
                        isLast: i === selectedAction?.length - 1,
                      });
                    }
                    return;
                  }

                  deleteHandler.mutate({ id: selectedAction, isLast: true });
                }}
                inlineModal={true}
                inlineButtonContainerStyles={{ marginBottom: "auto" }}
                confirmButtonText={t(($) => $.advertising["Confirm"])}
                confirmationMessage={
                  <p className="fs-4 py-2">
                    {t(($) => $.advertising["Are you sure you'd like to cancel the scheduled budget update?"])}
                  </p>
                }
                modalButtonText={
                  <div style={{ color: "#73706E", cursor: "pointer" }} className="mx-2" ref={cancelModalRef} />
                }
              />
            : null}
          </div>
        </Col>
      </Row>
      <Row>
        {scheduledActions?.length ?
          <BulkActionsTable
            marketPlace={marketPlace}
            tableRef={tableRef}
            tableId={"insightsTable"}
            titleCol={{
              id: "date",
              isStatic: true,
              Header: t(($) => $.advertising["Scheduled Change Period"]),
              accessor: "schedule",
              style: { width: "450px" },
              width: 300,
              Cell: ({ row }) => {
                const { start_date, end_date } = row._original;

                return (
                  <div className="d-flex align-items-center overflow-ellipsis">
                    {fmt.format(moment(start_date).utc().toDate())} to {fmt.format(moment(end_date).utc().toDate())}
                  </div>
                );
              },
              filterMethod: () => {
                return;
              },
              Filter: () => null,
            }}
            columnSpecs={[
              {
                key: "status",
                compareKey: null,
                options: {
                  metric: {
                    id: "status",
                    format: "text",
                    accessor: (d) => d.status,
                    options: {
                      headerClassName: "text-start justify-content-start",
                      className: "text-start d-flex align-items-center",
                      cellContainerClasses: "text-start justify-content-start py-3 align-items-center",
                      width: 100,
                    },
                    name: t(($) => $.advertising["Status"]),
                  },
                  formatter: (v) => {
                    const STATUS_LABELS = {
                      scheduled: t(($) => $.advertising["Scheduled"]),
                      active: t(($) => $.advertising["Active"]),
                      completed: t(($) => $.advertising["Completed"]),
                      cancelled: t(($) => $.advertising["Cancelled"]),
                    };
                    return STATUS_LABELS[v] ?? snakeCaseToSentenceCase(v);
                  },
                  hideFilter: true,
                },
              },
              {
                key: "change_type",
                compareKey: null,
                options: {
                  metric: {
                    id: "change_type",
                    format: "text",
                    accessor: (d) => d,

                    options: {
                      headerClassName: "text-start justify-content-start",
                      className: "text-start",
                      cellContainerClasses: "d-flex text-start justify-content-start h-100 align-items-center",
                      width: 750,
                    },
                    name: t(($) => $.advertising["Change Details"]),
                  },
                  formatter: (d) => {
                    const savedScheduledChange = d?.fields[0];

                    const type =
                      (
                        ["timebound_per_change", "timebound_flat_change", "manual_bid"].includes(
                          savedScheduledChange?.field_name,
                        )
                      ) ?
                        "bidBoost"
                      : "budgetChange";

                    if (type === "bidBoost") {
                      const isKeyword =
                        d.gvadvertisedkeyword_set?.length > 0 || d.walmartadgroupkeyword_set?.length > 0;
                      const isReduced =
                        (savedScheduledChange?.field_name === "timebound_per_change" &&
                          savedScheduledChange?.change_value < 1) ||
                        (savedScheduledChange?.field_name !== "timebound_per_change" &&
                          savedScheduledChange?.change_value < 0);
                      const amount =
                        savedScheduledChange?.field_name === "timebound_per_change" ?
                          `${parseInt(Math.abs(savedScheduledChange?.change_value * 100 - 100))}%`
                        : formatCurrency(Math.abs(savedScheduledChange?.change_value), {
                            marketPlace: marketPlace,
                          });
                      const message =
                        isKeyword ?
                          isReduced ?
                            t(
                              ($) =>
                                $.advertising["The bid for keywords will be reduced by {amount} during this period."],
                              {
                                count: d?.entityCount,
                                amount,
                              },
                            )
                          : t(
                              ($) =>
                                $.advertising["The bid for keywords will be boosted by {amount} during this period."],
                              {
                                count: d?.entityCount,
                                amount,
                              },
                            )
                        : isReduced ?
                          t(
                            ($) => $.advertising["The bid for targets will be reduced by {amount} during this period."],
                            {
                              count: d?.entityCount,
                              amount,
                            },
                          )
                        : t(
                            ($) => $.advertising["The bid for targets will be boosted by {amount} during this period."],
                            {
                              count: d?.entityCount,
                              amount,
                            },
                          );

                      return (
                        <>
                          {message}
                          <span
                            onClick={() => jumpToTab(isKeyword ? "keywords" : "targets")}
                            className="mx-3"
                            style={{
                              color: trellisPalette[10],
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {t(($) => $.advertising["View"])}
                          </span>
                        </>
                      );
                    }

                    const isIncrease =
                      savedScheduledChange?.change_type === "percentage_change" ?
                        savedScheduledChange?.change_value >= 1
                      : savedScheduledChange?.change_value >= 0;
                    const amount =
                      savedScheduledChange?.change_type === "percentage_change" ?
                        `${Math.round(Math.abs(savedScheduledChange?.change_value * 100 - 100))}%`
                      : formatCurrency(Math.abs(savedScheduledChange?.change_value), {
                          marketPlace: marketPlace,
                        });
                    const dailyAmount = formatCurrency(state.dailyBudget, { marketPlace: marketPlace });
                    const monthlyAmount = formatCurrency(state.dailyBudget * 30, { marketPlace: marketPlace });

                    if (d.status === "active") {
                      return isIncrease ?
                          t(
                            ($) =>
                              $.advertising[
                                "The advertising budget is currently increased by {amount} and set to {dailyAmount} a day ({monthlyAmount} a month)."
                              ],
                            { amount, dailyAmount, monthlyAmount },
                          )
                        : t(
                            ($) =>
                              $.advertising[
                                "The advertising budget is currently decreased by {amount} and set to {dailyAmount} a day ({monthlyAmount} a month)."
                              ],
                            { amount, dailyAmount, monthlyAmount },
                          );
                    }

                    const projectedDailyAmount = formatCurrency(
                      Math.abs(
                        savedScheduledChange?.change_type === "percentage_change" ?
                          state.dailyBudget * savedScheduledChange?.change_value
                        : parseInt(state.dailyBudget) + savedScheduledChange?.change_value,
                      ),
                      { marketPlace: marketPlace },
                    );
                    const projectedMonthlyAmount = formatCurrency(
                      savedScheduledChange?.change_type === "percentage_change" ?
                        parseInt(state.dailyBudget) * MONTHLY_BUDGET_MULTIPLIER * savedScheduledChange?.change_value
                      : parseInt(state.dailyBudget) * MONTHLY_BUDGET_MULTIPLIER +
                          d.fields?.find((f) => f.field_name === "monthly_budget")?.change_value,
                      { marketPlace: marketPlace },
                    );

                    return isIncrease ?
                        t(
                          ($) =>
                            $.advertising[
                              "The advertising budget will be increased by {amount} and will be set to {dailyAmount} a day ({monthlyAmount} a month)."
                            ],
                          {
                            amount,
                            dailyAmount: projectedDailyAmount,
                            monthlyAmount: projectedMonthlyAmount,
                          },
                        )
                      : t(
                          ($) =>
                            $.advertising[
                              "The advertising budget will be decreased by {amount} and will be set to {dailyAmount} a day ({monthlyAmount} a month)."
                            ],
                          {
                            amount,
                            dailyAmount: projectedDailyAmount,
                            monthlyAmount: projectedMonthlyAmount,
                          },
                        );
                  },
                  hideFilter: true,
                },
              },

              {
                key: "change_type",
                compareKey: null,
                options: {
                  metric: {
                    id: "change_type",
                    format: "text",
                    accessor: (d) => d,
                    options: {
                      headerClassName: "text-start justify-content-start",
                      className: "text-start d-flex align-items-center",
                      cellContainerClasses: "text-start justify-content-start align-items-center",
                      width: 100,
                    },
                    name: t(($) => $.advertising["Actions"]),
                  },
                  formatter: (d) => {
                    if (!userCanUpdateAds) {
                      return null;
                    }

                    const scheduledAction = scheduledActions?.find((a) => a.id === d.id || d.id.includes(a.id));

                    return (
                      <div
                        className="d-flex"
                        onChange={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div
                          style={{
                            color: "#73706E",
                            cursor: "pointer",
                            visibility:
                              (
                                scheduledAction?.fields?.find((f) =>
                                  ["timebound_per_change", "timebound_flat_change", "manual_bid"].includes(
                                    f.field_name,
                                  ),
                                )
                              ) ?
                                "hidden"
                              : "inherit",
                          }}
                          className="mx-2"
                        >
                          <HoverInfo
                            icon={
                              <FaPencil
                                className={css({ cursor: "pointer" })}
                                onClick={() => {
                                  const changeValue = scheduledAction?.fields?.find(
                                    (f) => f.field_name === "daily_budget",
                                  )?.change_value;
                                  const changeUnit = scheduledAction?.fields?.find(
                                    (f) => f.field_name === "daily_budget",
                                  )?.change_type;

                                  scheduleModal.show({
                                    title: t(($) => $.advertising["Update Scheduled Budget Change"]),
                                    onConfirm: (formData) =>
                                      processScheduledBudgetUpdate(formData, scheduledAction?.id),
                                    actionTarget: t(($) => $.advertising["Daily Budget"]),
                                    schedulingDialogue: true,
                                    initialValues: {
                                      actionType:
                                        changeUnit === "percentage_change" ?
                                          changeValue >= 1 ?
                                            "increase"
                                          : "decrease"
                                        : changeValue >= 0 ? "increase"
                                        : "decrease",
                                      actionValue:
                                        changeUnit === "percentage_change" ?
                                          Math.abs(changeValue * 100) - 100
                                        : Math.abs(changeValue),
                                      actionUnit: changeUnit === "flat_change" ? "currency" : "percent",
                                      startDate: moment.utc(scheduledAction?.start_date).format("YYYY-MM-DD"),
                                      endDate: moment.utc(scheduledAction?.end_date).format("YYYY-MM-DD"),
                                    },
                                    disabledFields:
                                      d.status === "active" ?
                                        ["actionType", "actionValue", "actionUnit", "startDate"]
                                      : undefined,
                                  });
                                }}
                              />
                            }
                          >
                            {t(($) => $.advertising["Update Scheduled Action"])}
                          </HoverInfo>
                        </div>

                        <div
                          style={{
                            color: "#73706E",
                            cursor: "pointer",
                          }}
                          className="mx-2"
                        >
                          <HoverInfo
                            className={css({ cursor: "pointer" })}
                            icon={
                              <FaRegCalendarTimes
                                onClick={() => {
                                  setSelectedAction(d.id);
                                  cancelModalRef.current.click();
                                }}
                              />
                            }
                          >
                            {t(($) => $.advertising["Cancel Scheduled Action"])}
                          </HoverInfo>
                        </div>
                      </div>
                    );
                  },
                  hideFilter: true,
                },
              },

              {
                key: "created_by",
                compareKey: null,
                options: {
                  metric: {
                    id: "change_type",
                    format: "text",
                    accessor: (d) => d,
                    options: {
                      headerClassName: "text-start justify-content-start",
                      className: "text-start d-flex align-items-center",
                      cellContainerClasses: "text-start justify-content-start p-3 align-items-center",
                      width: 200,
                    },
                    name: t(($) => $.advertising["Created By"]),
                  },
                  formatter: (data) => {
                    const { first_name, last_name, is_staff } = data.created_by;
                    return (
                      <>
                        {first_name} {last_name}{" "}
                        <span>{is_staff ? ` ${t(($) => $.advertising["(Trellis Staff)"])}` : ""}</span>
                      </>
                    );
                  },
                  hideFilter: true,
                },
              },
            ]}
            data={tableData}
            bulkActionOptions={[]}
            hideFilterToggleIcon={true}
            isSelectable={false}
            isExpandable={false}
            selected={selectedAction}
            setSelected={setSelectedAction}
            selectionKey={"id"}
            hidePagination={true}
            getSearchCriteria={(row) => {
              const {
                advertisement: { name, id },
              } = row;
              return `${name} ${id}`;
            }}
            tableRowDescription={t(($) => $.advertising["Scheduled Actions"])}
            minRows={0}
          />
        : null}
      </Row>
    </>
  );
};

export default ScheduledBudgetSummary;
